import defaultNotificationSound from '../assets/sounds/default_notification.mp3';

class AudioService {
  constructor() {
    this.audio = null;
    this.isInitialized = false;
    this.isAudioSupported = false;
    this.soundUrls = [
      defaultNotificationSound
    ];
    this.workingSoundUrl = null;
    this.audioContext = null;
  }

  /**
   * Initialize the audio service
   * This should be called after user interaction to comply with browser autoplay policies
   */
  async initialize() {
    if (this.isInitialized) {
      console.log('🔊 AudioService: Already initialized', this.getStatus());
      return;
    }

    console.log('🔊 AudioService: Starting initialization...');

    // Test if audio is supported
    if (!window.Audio) {
      console.warn('🔊 AudioService: Audio not supported in this browser');
      this.isInitialized = true;
      return;
    }

    // Try to find a working audio file
    for (const soundUrl of this.soundUrls) {
      try {
        console.log('🔊 AudioService: Testing sound URL:', soundUrl);
        const testAudio = new Audio(soundUrl);

        // Test if the audio can be loaded
        await new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            reject(new Error('Audio load timeout'));
          }, 3000);

          testAudio.addEventListener('canplaythrough', () => {
            clearTimeout(timeout);
            console.log('🔊 AudioService: Audio file loaded successfully');
            resolve();
          }, { once: true });

          testAudio.addEventListener('error', (e) => {
            clearTimeout(timeout);
            console.error('🔊 AudioService: Audio file load error:', e);
            reject(e);
          }, { once: true });

          testAudio.load();
        });

        // If we get here, the audio file works
        this.workingSoundUrl = soundUrl;
        this.isAudioSupported = true;
        console.log('🔊 AudioService: Audio initialized successfully!', this.getStatus());
        break;

      } catch (error) {
        console.error('🔊 AudioService: Failed to load audio file:', error);
        continue;
      }
    }

    this.isInitialized = true;

    if (!this.isAudioSupported) {
      console.warn('🔊 AudioService: No working audio file found');
    }
  }

  /**
   * Play a single notification sound with a new Audio instance
   */
  async playSingleSound() {
    console.log('🔊 AudioService: playSingleSound called', {
      isInitialized: this.isInitialized,
      isAudioSupported: this.isAudioSupported,
      workingSoundUrl: this.workingSoundUrl
    });

    if (!this.isInitialized) {
      console.log('🔊 AudioService: Not initialized, initializing now...');
      await this.initialize();
    }

    if (!this.isAudioSupported || !this.workingSoundUrl) {
      console.warn('🔊 AudioService: Cannot play sound - audio not supported or no working sound URL');
      return;
    }

    try {
      console.log('🔊 AudioService: Creating new Audio instance...');
      // Create a new Audio instance for each play to avoid conflicts
      const audio = new Audio(this.workingSoundUrl);
      audio.volume = 0.8; // Slightly lower volume to be less jarring

      console.log('🔊 AudioService: Attempting to play sound...');
      // Play the sound with timeout
      const playPromise = audio.play();

      if (playPromise !== undefined) {
        // Add timeout to prevent hanging
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Audio play timeout')), 5000);
        });

        await Promise.race([playPromise, timeoutPromise]);
        console.log('🔊 AudioService: Sound played successfully! ✓');
      }
    } catch (error) {
      console.error('🔊 AudioService: Error playing sound:', error);
      // Don't throw error - just continue silently
    }
  }

  /**
   * Play notification sound once
   */
  async playNotificationSound() {
    console.log('🔊 AudioService: playNotificationSound called');
    try {
      await this.playSingleSound();
    } catch (error) {
      console.error('🔊 AudioService: Notification sound failed:', error);
      // Gracefully handle audio failures - don't break the notification system
    }
  }

  /**
   * Get or create a shared AudioContext (lazily initialized).
   * Must be called after a user gesture for browser autoplay compliance.
   */
  getAudioContext() {
    if (!this.audioContext || this.audioContext.state === 'closed') {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return null;
      this.audioContext = new AudioCtx();
    }
    if (this.audioContext.state === 'suspended') {
      this.audioContext.resume().catch(() => {});
    }
    return this.audioContext;
  }

  /**
   * Play a synthesised overdue alert tone using the Web Audio API.
   * Three short ascending beeps: urgent but not obnoxious.
   */
  async playOverdueAlert() {
    try {
      const ctx = this.getAudioContext();
      if (!ctx) return;

      const now = ctx.currentTime;
      const beeps = [
        { freq: 880, start: 0, dur: 0.12 },
        { freq: 1047, start: 0.18, dur: 0.12 },
        { freq: 1319, start: 0.36, dur: 0.18 },
      ];

      beeps.forEach(({ freq, start, dur }) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();

        osc.type = 'sine';
        osc.frequency.value = freq;

        gain.gain.setValueAtTime(0, now + start);
        gain.gain.linearRampToValueAtTime(0.35, now + start + 0.02);
        gain.gain.linearRampToValueAtTime(0, now + start + dur);

        osc.connect(gain);
        gain.connect(ctx.destination);

        osc.start(now + start);
        osc.stop(now + start + dur + 0.01);
      });
    } catch (error) {
      console.error('🔊 AudioService: Overdue alert sound failed:', error);
    }
  }

  /**
   * Check if audio is supported and initialized
   */
  isAvailable() {
    return this.isInitialized && this.isAudioSupported && this.workingSoundUrl !== null;
  }

  /**
   * Get the current audio status for debugging
   */
  getStatus() {
    return {
      isInitialized: this.isInitialized,
      isAudioSupported: this.isAudioSupported,
      workingSoundUrl: this.workingSoundUrl,
      isAvailable: this.isAvailable()
    };
  }
}

// Create singleton instance
const audioService = new AudioService();

export default audioService;
